/**
 * Runtime feature flags (DB-backed, super-admin toggleable, no redeploy).
 *
 * Bridge between an ASYNC source (the `feature_flags` table) and SYNC consumers
 * (`resolveMediaUrl` runs in render AND in the non-React prefetch coordinator).
 * A module-level cache is read synchronously via `getFlag`; the loader populates
 * it. The cache defaults to "off", so any read before the loader resolves is a
 * safe pass-through identical to having no flags at all.
 *
 * Invariant: the loader is awaited in the root route `beforeLoad` AFTER the
 * session check, so the cache is hot before any authenticated route paints.
 * Unauthenticated paths (/login, …) never await it and never play video.
 */

import { type QueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface FlagState {
  enabled: boolean;
  value: string;
}

const DEFAULTS: Record<string, FlagState> = {
  hls_cache_bust: { enabled: false, value: '' },
  // Persisted-entitlement cache buster. Bump `value` (admin/flags) after an
  // entitlement backfill/change to force every client to drop its stale
  // IndexedDB snapshot on next load. See lib/entitlement-cache-bust.ts.
  entitlement_cache_bust: { enabled: false, value: '' },
  // Xero integration — the whole feature (connect screen, sync, finance
  // widgets). Ships dark; flip on at /admin/flags. Every Xero surface gates on
  // this via `isXeroEnabled()`. AUDIENCE lives in `value`: empty = everyone;
  // a list of emails = ONLY those users + platform staff (beta gating, editable
  // at /admin/flags with no deploy).
  xero_integration: { enabled: false, value: '' },
  // Prime Score roadmap (F-131) — the Roadmap and History tabs on the diagnostic,
  // their routes, and every background snapshot/recompute write. Ships dark; flip
  // on at /admin/flags per environment. AUDIENCE lives in `value` exactly as for
  // Xero: empty = everyone; a list of emails = ONLY those users + platform staff.
  prime_score_roadmap: { enabled: false, value: '' },
  /**
   * The next-move coaching card on Home (T11). Owner and admin only; the card itself
   * also checks the role. `value` is an email allowlist like the Xero flag.
   */
  coaching_next_move: { enabled: false, value: '' },
  /**
   * Masquerade — a super admin entering a member's account (Support Access, epic
   * 86d467vyj). Ships dark; flip on at /admin/flags per environment.
   *
   * Plain on/off, NOT an audience flag: Tim's ruling (27 Sep 2026) is that every super
   * admin gets it once it's on, rather than a per-person allowlist. So `value` is unused
   * and this key stays out of AUDIENCE_FLAGS.
   *
   * Display gate only. The edge function re-checks super_admin on every call and is the
   * real boundary — turning this on cannot grant access the server would refuse, and
   * turning it off does not revoke a session already created.
   */
  masquerade: { enabled: false, value: '' },
  /**
   * Group ticket purchases for paid events (hermes migration 675): one buyer pays
   * once for up to 10 seats. Plain on/off. The edge functions read the same row
   * and are the real gate; here it only decides whether "Buy tickets for others"
   * is shown to a member who is already going.
   */
  event_group_tickets: { enabled: false, value: '' },
  /**
   * Speedies (F-129): every Speedy surface, plus Chat moving to its own menu item at /chat. Plain
   * on/off; `value` is unused. Display gate only: hermes reads the same row and refuses Speedy
   * uploads and search while it's off.
   */
  speedies: { enabled: false, value: '' },
};

/** Flags whose `value` is a beta email allowlist rather than a cache-buster. */
export const AUDIENCE_FLAGS = ['xero_integration', 'prime_score_roadmap', 'coaching_next_move'] as const;
export type AudienceFlagKey = (typeof AUDIENCE_FLAGS)[number];

// Synchronously-readable cache. Seeded with safe defaults.
const flagCache: Record<string, FlagState> = { ...DEFAULTS };

/** Synchronous flag read. Returns a safe `{enabled:false, value:''}` if unknown. */
export function getFlag(key: string): FlagState {
  return flagCache[key] ?? { enabled: false, value: '' };
}

/** Parse a flag's `value` as an email allowlist: comma/space/semicolon separated,
    lowercased; anything without an @ is ignored. Empty list = no restriction. */
export function parseFlagAudience(value: string): string[] {
  return value
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.includes('@'));
}

/** Pure audience decision: no list = everyone; else listed emails or staff. */
export function audienceAllows(
  list: string[],
  email: string | null,
  isStaff: boolean,
): boolean {
  if (list.length === 0) return true;
  if (isStaff) return true;
  return email !== null && list.includes(email.toLowerCase());
}

/**
 * Pure end-to-end audience decision over the RAW flag value (F-124 review #1).
 * Empty value = unrestricted (ratified backward-compat: other flags use `value`
 * as a cache-buster). A NON-empty value that parses to zero emails is an
 * operator error (e.g. "beta", a paste that lost its addresses) and FAILS
 * CLOSED to staff — never open to every authenticated user.
 */
export function resolveAudience(
  rawValue: string,
  email: string | null,
  isStaff: boolean,
): boolean {
  if (rawValue.trim() === '') return true;
  const list = parseFlagAudience(rawValue);
  if (list.length === 0) return isStaff;
  return audienceAllows(list, email, isStaff);
}

// Resolved per audience flag during loadFeatureFlags (post-session, pre-paint). Missing
// = true so an UNRESTRICTED flag behaves exactly as before; a restricted flag flips it
// before any authed route paints (fail direction guarded by the flag itself defaulting
// to off).
const audienceAllowed: Partial<Record<AudienceFlagKey, boolean>> = {};

function flagLiveForUser(key: AudienceFlagKey): boolean {
  return getFlag(key).enabled && (audienceAllowed[key] ?? true);
}

/**
 * Whether the Xero integration is live FOR THIS USER. Every Xero surface — the
 * connect route, nav entries, finance widgets — gates on this. Reads the sync
 * cache, which the root `beforeLoad` warms before any authed route paints, and
 * which defaults to `false` — so an unresolved flag fails closed (hidden).
 * When the flag's `value` carries a beta allowlist, only listed emails and
 * platform staff (request_user_is_admin) pass.
 */
export function isXeroEnabled(): boolean {
  return flagLiveForUser('xero_integration');
}

/**
 * Whether the Prime Score roadmap (F-131) is live FOR THIS USER: the Roadmap and
 * History tabs, their routes, and the background snapshot/recompute writes. A
 * rollout gate, not access control — the roadmap RPCs keep their paid/role checks.
 * Same cache and audience rules as the Xero flag; cold cache fails closed.
 */
/**
 * Whether the finance FORECAST area is open (Tim, 13 Oct 2026: "for the time being, forecast won't
 * be used"). Off: the Forecast tab is shown greyed out and can't be clicked, and every
 * /finance/forecast route redirects to the dashboard. Turn it back on by returning true (or by
 * moving it onto flagLiveForUser like the flags around it).
 */
export function isForecastEnabled(): boolean {
  return false;
}

export function isRoadmapEnabled(): boolean {
  return flagLiveForUser('prime_score_roadmap');
}

/**
 * Whether the next-move coaching card (T11) is live FOR THIS USER. Same cache and audience
 * rules as the Xero flag; cold cache fails closed. The card still requires an owner or admin.
 */
export function isCoachingNextMoveEnabled(): boolean {
  return flagLiveForUser('coaching_next_move');
}

/**
 * Whether the masquerade action is live. Reads the flag directly rather than through
 * `flagLiveForUser`, because this is not an audience flag — every super admin gets it
 * once it's on (Tim's ruling, 27 Sep 2026).
 *
 * A rollout gate and a display gate, never the access control. The caller must ALSO be a
 * super admin, and the edge function re-checks that on every call. Cold cache fails closed,
 * so the button is hidden until the flags have loaded.
 */
export function isMasqueradeEnabled(): boolean {
  return getFlag('masquerade').enabled;
}

/** Whether group ticket purchases are on (display gate only; the server re-checks). */
export function isGroupTicketsEnabled(): boolean {
  return getFlag('event_group_tickets').enabled;
}

/** Whether Speedies are on (display gate only; a cold cache reads off). */
export function isSpeediesEnabled(): boolean {
  return getFlag('speedies').enabled;
}

/** Test seam: reset the per-flag audience decisions (the cache itself is reset by re-loading). */
export function resetFlagAudiences(): void {
  for (const key of AUDIENCE_FLAGS) delete audienceAllowed[key];
}

export interface FlagRow {
  key: string;
  enabled: boolean;
  value: string;
  description?: string | null;
  updated_at?: string | null;
}

function writeThrough(rows: FlagRow[]): void {
  for (const r of rows) {
    flagCache[r.key] = { enabled: !!r.enabled, value: r.value ?? '' };
  }
}

/**
 * react-query options for the flag set. The queryFn write-throughs into the
 * sync cache, so both `queryClient.ensureQueryData` (root loader) and
 * `useFeatureFlags` (admin UI) keep `getFlag` current.
 */
export const featureFlagsQueryOptions = {
  queryKey: ['feature-flags'] as const,
  queryFn: async (): Promise<FlagRow[]> => {
    const { data, error } = await supabase
      .from('feature_flags')
      .select('key, enabled, value, description, updated_at');
    if (error) throw error;
    const rows = (data ?? []) as FlagRow[];
    writeThrough(rows);
    return rows;
  },
  staleTime: 5 * 60 * 1000,
};

/** react-query hook over the flag set (write-throughs to the sync cache). For admin UI. */
export function useFeatureFlags() {
  return useQuery(featureFlagsQueryOptions);
}

/**
 * Load flags into the sync cache. Errors are swallowed (logged) so a flag-fetch
 * failure never blocks navigation — defaults stay in effect.
 */
export async function loadFeatureFlags(queryClient: QueryClient): Promise<void> {
  try {
    await queryClient.ensureQueryData({
      ...featureFlagsQueryOptions,
      retry: false,
    });
  } catch (err) {
    console.error('[feature-flags] load failed; using defaults', err);
  }
  // Beta-audience resolution, one flag at a time (each decides for itself; no early
  // return skips the next key). The session email and the staff RPC are fetched lazily
  // and shared, so a load costs at most one round trip of each however many flags are
  // restricted.
  let email: string | null | undefined;
  let isStaff: boolean | undefined;
  const getEmail = async (): Promise<string | null> => {
    if (email === undefined) {
      const { data: sessionData } = await supabase.auth.getSession();
      email = sessionData.session?.user?.email ?? null;
    }
    return email;
  };
  const getIsStaff = async (): Promise<boolean> => {
    if (isStaff === undefined) {
      const { data } = await supabase.rpc('request_user_is_admin');
      isStaff = data === true;
    }
    return isStaff;
  };
  for (const key of AUDIENCE_FLAGS) {
    try {
      audienceAllowed[key] = await resolveFlagAudience(getFlag(key), getEmail, getIsStaff);
    } catch (err) {
      console.error(`[feature-flags] audience resolve failed for ${key}; failing closed`, err);
      audienceAllowed[key] = false;
    }
  }
}

/**
 * One flag's audience decision. Off or truly empty = allowed (the enabled check hides an
 * off flag anyway; empty = unrestricted, backward-compatible). A restricted (or junk)
 * value resolves via the pure decision: the session email first, the staff RPC only when
 * the email does not settle it.
 */
async function resolveFlagAudience(
  flag: FlagState,
  getEmail: () => Promise<string | null>,
  getIsStaff: () => Promise<boolean>,
): Promise<boolean> {
  if (!flag.enabled) return true;
  if (flag.value.trim() === '') return true;
  const list = parseFlagAudience(flag.value);
  const email = await getEmail();
  if (list.length > 0 && email && list.includes(email.toLowerCase())) return true;
  return resolveAudience(flag.value, email, await getIsStaff());
}

/**
 * Append the HLS cache-buster (`?v=<value>`) to ANY media URL when the
 * `hls_cache_bust` flag is enabled with a non-empty value.
 *
 * This must be applied at the per-request level — i.e. to the master manifest,
 * the variant playlists, AND every segment — because hls.js resolves child URLs
 * relative to the master and does NOT propagate the master's query string. A
 * fresh `?v=` is a guaranteed cache miss at both the browser and Cloudflare
 * (verified: CF keys on the query string), so it forces an origin refetch of the
 * known-good bytes through every cache layer. Bump the value to re-bust.
 *
 * Applied via the hls.js loader (player) and the prefetch URL builders so both
 * request the identical busted URLs.
 */
export function withCacheBust(url: string): string {
  const flag = getFlag('hls_cache_bust');
  if (!flag.enabled || !flag.value) return url;
  try {
    const u = new URL(url);
    u.searchParams.set('v', flag.value);
    return u.toString();
  } catch {
    return url;
  }
}
